import { Controller } from "@hotwired/stimulus"

// data-controller="chat-form"
//   <form>
//     <textarea data-chat-form-target="input"
//               data-action="keydown->chat-form#submitOnEnter
//                            input->chat-form#autogrow">…</textarea>
//     <button>Send</button>
//   </form>
//
// - Resets the input on a successful Turbo submit and re-focuses it so the
//   user can immediately type the next question.
// - Enter submits the form. Shift+Enter inserts a newline so users can
//   write multi-paragraph questions (chat conventions everyone knows from
//   Slack / iMessage / ChatGPT).
// - Autogrow: textarea height tracks content up to 5 lines, then scrolls.
export default class extends Controller {
  static targets = ["input"]
  static values = { maxRows: { type: Number, default: 5 } }

  connect() {
    this.boundReset = this.reset.bind(this)
    this.element.addEventListener("turbo:submit-end", this.boundReset)
    if (this.hasInputTarget) this.autogrow()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.boundReset)
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    // IME composition (e.g. Japanese): never intercept.
    if (event.isComposing || event.keyCode === 229) return
    event.preventDefault()
    if (this.inputTarget.value.trim() === "") return
    this.element.requestSubmit()
  }

  // Reset to 1 line, measure scrollHeight, set height up to a max of
  // `maxRows` line-heights — once that ceiling is hit, the textarea
  // becomes scrollable.
  autogrow() {
    const el = this.inputTarget
    const styles  = window.getComputedStyle(el)
    const lineHt  = parseFloat(styles.lineHeight) || 20
    const padTop  = parseFloat(styles.paddingTop) || 0
    const padBot  = parseFloat(styles.paddingBottom) || 0
    const borderY = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0)
    const maxH    = (lineHt * this.maxRowsValue) + padTop + padBot + borderY

    el.style.height = "auto"
    const next = Math.min(el.scrollHeight + borderY, maxH)
    el.style.height = `${next}px`
    el.style.overflowY = (el.scrollHeight + borderY > maxH) ? "auto" : "hidden"
  }

  reset(event) {
    if (!event.detail?.success) return
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
      this.autogrow()
    }
  }
}
